import { useMutation } from '@apollo/client';
import styled from '@emotion/styled';
import { CircularProgress, Grid } from '@material-ui/core';
import { FC, useEffect, useState } from 'react';
import { ConversionGoal, PutConversionGoalDocument } from '../../generated/graphql';
import {
  DrawerButton,
  DrawerCancelButton,
  DrawerContainer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  StyledDrawer,
} from '../component/primitive/Drawer';
import getComparatorType from '../../utility/getComparatorType';
import StyledBackdrop from '../component/compound/GoalSetting/StyledBackdrop';
import SelectedTag from '../../types/SelectedTag';
import TagContent from '../component/compound/GoalSetting/Drawer/TagContent';
import NameContent from '../component/compound/GoalSetting/Drawer/NameContent';
import UrlMatcher from '../../types/UrlMatcher';
import UrlContent from '../component/compound/GoalSetting/Drawer/UrlContent';
import EventMatcher from '../../types/EventMatcher';
import FilterType from '../../types/FilterType';
import EventContent from '../component/compound/GoalSetting/Drawer/EventContent';
import globalState from '../../operations/commands';

type DrawerType = 'NAME' | 'TAG' | 'URL' | 'EVENT';

type Props = {
  goal: ConversionGoal;
  tags: SelectedTag[];
  refetch: () => void;
  open: boolean;
  setOpen: (value: React.SetStateAction<boolean>) => void;
  drawerType?: DrawerType;
  className?: string;
};

const GoalSettingEditDrawer: FC<Props> = ({ goal, tags, refetch, open, setOpen, drawerType, className }) => {
  const [header, setHeader] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [urlMatchers, setUrlMatchers] = useState<UrlMatcher[]>([
    { id: '', expression: '', isInclusion: true, matchType: 'simple', destroy: false },
    { id: '', expression: '', isInclusion: false, matchType: 'simple', destroy: false },
  ]);
  const [eventMatchers, setEventMatchers] = useState<EventMatcher[]>([
    {
      comparator: 'et_event',
      event: 'click',
      type: 'EtEvent',
      name: '',
      conditions: [],
      etSelector: {
        data: {
          conditions: [],
        },
        type: 'EtSelector',
      },
    },
  ]);
  const [selectedTags, setSelectedTags] = useState<SelectedTag[]>(tags);
  const [radioValue, setRadioValue] = useState('all');
  const [siteViewerUrl, setSiteViewerUrl] = useState('');

  useEffect(() => {
    setTitle(goal.title);
    goal.description && setDescription(goal.description);
    selectedTags.map((tag, index) => {
      if (goal.goal_tags && goal.goal_tags.findIndex((goalTag) => goalTag?.id === tag.tag.id) < 0) {
        selectedTags[index].selected = false;
      } else {
        selectedTags[index].selected = true;
      }
    });
    setSelectedTags(selectedTags.slice());

    goal.trigger.url_matchers
      ? setUrlMatchers(
          goal.trigger.url_matchers.map((urlMatcher) => {
            return {
              id: urlMatcher?.id,
              expression: urlMatcher?.expression,
              isInclusion: urlMatcher?.is_inclusion,
              matchType: urlMatcher?.match_type,
              destroy: false,
            } as UrlMatcher;
          }),
        )
      : setUrlMatchers([]);

    goal.trigger.event_matchers
      ? setEventMatchers(
          // @ts-ignore
          goal.trigger.event_matchers.map((eventMatcher) => {
            return {
              comparator: eventMatcher?.data.comparator,
              event: eventMatcher?.data.event,
              type: eventMatcher?.type,
              name: eventMatcher?.data.name,
              conditions: eventMatcher?.data.conditions
                ?.map((condition) => {
                  if (condition?.comparator !== 'et_selector') {
                    return {
                      data: {
                        args: condition?.args?.[0],
                        method: condition?.method,
                        comparator: condition?.comparator,
                        type: 'string',
                      },
                    };
                  } else return undefined;
                })
                .filter(Boolean),
              etSelector: {
                data: {
                  conditions: eventMatcher?.data?.conditions
                    ?.find((condition) => condition?.comparator === 'et_selector')
                    ?.conditions?.map((condition) => {
                      return {
                        data: {
                          args: condition?.args?.[0],
                          method: condition?.method,
                          type: 'string',
                        },
                        type: getComparatorType(condition?.comparator) as FilterType,
                      };
                    }),
                },
                type: 'EtSelector',
              },
              id: eventMatcher?.id,
            };
          }),
        )
      : setEventMatchers([]);

    goal.trigger.device_type && setRadioValue(goal.trigger.device_type);
  }, [goal]);

  useEffect(() => {
    if (drawerType === 'NAME') {
      setHeader('ゴール名と概要の設定');
    } else if (drawerType === 'TAG') {
      setHeader('コンバージョンタグの設定');
    } else if (drawerType === 'URL') {
      setHeader('対象のURLの追加');
    } else if (drawerType === 'EVENT') {
      setHeader('イベントの設定');
    }
  }, [drawerType]);

  const togglePostDrawer = (openPostDrawer: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setOpen(openPostDrawer);
  };

  const closePostDrawer = () => {
    initializeGoal();
    setOpen(false);
  };

  const initializeGoal = () => {
    setTitle(goal.title);
    setSiteViewerUrl('');
    goal.description && setDescription(goal.description);
    selectedTags.map((tag, index) => {
      if (goal.goal_tags && goal.goal_tags.findIndex((goalTag) => goalTag?.id === tag.tag.id) < 0) {
        selectedTags[index].selected = false;
      } else {
        selectedTags[index].selected = true;
      }
    });
    setSelectedTags(selectedTags.slice());

    goal.trigger.url_matchers
      ? setUrlMatchers(
          goal.trigger.url_matchers.map((urlMatcher) => {
            return {
              expression: urlMatcher?.expression,
              isInclusion: urlMatcher?.is_inclusion,
              matchType: urlMatcher?.match_type,
              destroy: false,
              id: urlMatcher?.id,
            } as UrlMatcher;
          }),
        )
      : setUrlMatchers([]);

    goal.trigger.event_matchers
      ? setEventMatchers(
          // @ts-ignore
          goal.trigger.event_matchers.map((eventMatcher) => {
            return {
              comparator: eventMatcher?.data.comparator,
              event: eventMatcher?.data.event,
              type: eventMatcher?.type,
              name: eventMatcher?.data.name,
              conditions: eventMatcher?.data.conditions
                ?.map((condition) => {
                  if (condition?.comparator !== 'et_selector') {
                    return {
                      data: {
                        args: condition?.args?.[0],
                        method: condition?.method,
                        comparator: condition?.comparator,
                        type: 'string',
                      },
                    };
                  } else return undefined;
                })
                .filter(Boolean),
              etSelector: {
                data: {
                  conditions: eventMatcher?.data?.conditions
                    ?.find((condition) => condition?.comparator === 'et_selector')
                    ?.conditions?.map((condition) => {
                      return {
                        data: {
                          args: condition?.args?.[0],
                          method: condition?.method,
                          type: 'string',
                        },
                        type: getComparatorType(condition?.comparator) as FilterType,
                      };
                    }),
                },
                type: 'EtSelector',
              },
              id: eventMatcher?.id,
            };
          }),
        )
      : setEventMatchers([]);

    goal.trigger.device_type && setRadioValue(goal.trigger.device_type);
  };

  const addInclusionUrl = () => {
    urlMatchers.push({ id: '', expression: '', isInclusion: true, matchType: 'simple', destroy: false });
    setUrlMatchers(urlMatchers.slice());
  };

  const addInclusionUrlBySiteViewer = (siteViewerUrl: string) => {
    urlMatchers.push({ id: '', expression: siteViewerUrl, isInclusion: true, matchType: 'simple', destroy: false });
    setUrlMatchers(urlMatchers.slice());
  };

  const addExclusionUrl = () => {
    urlMatchers.push({ id: '', expression: '', isInclusion: false, matchType: 'simple', destroy: false });
    setUrlMatchers(urlMatchers.slice());
  };

  const removeUrl = (index: number) => {
    urlMatchers[index].destroy = true;
    setUrlMatchers(urlMatchers.slice());
  };

  const addEventMatcher = () => {
    eventMatchers.push({
      comparator: 'EtContain',
      event: 'click',
      type: 'EtEvent',
      name: '',
      conditions: [],
      etSelector: {
        data: {
          conditions: [],
        },
        type: 'EtSelector',
      },
    });
    setEventMatchers(eventMatchers.slice());
  };

  const addEventMatcherBySiteViewer = (newEventMatchers: EventMatcher[]) => {
    eventMatchers.push(...newEventMatchers);
    setEventMatchers(eventMatchers.slice());
  };

  const addEventFilter = (index: number) => {
    eventMatchers[index].conditions.push({
      data: {
        args: '',
        method: 'attrClass',
        type: 'string',
        comparator: 'EtContain',
      },
    });
    setEventMatchers(eventMatchers.slice());
  };

  const addEtSelector = (index: number) => {
    eventMatchers[index].etSelector.data.conditions.push({
      data: {
        args: '',
        method: 'attrClass',
        type: 'string',
      },
      type: 'EtContain',
    });
    setEventMatchers(eventMatchers.slice());
  };

  const removeEventMatcher = (index: number) => {
    eventMatchers[index].destroy = true;
    setEventMatchers(eventMatchers.slice());
  };

  const removeEventFilter = (eventIndex: number, filterIndex: number) => {
    eventMatchers[eventIndex].conditions.splice(filterIndex, 1);
    setEventMatchers(eventMatchers.slice());
  };

  const removeEtSelector = (index: number, etSelectorIndex: number) => {
    eventMatchers[index].etSelector.data.conditions.splice(etSelectorIndex, 1);
    setEventMatchers(eventMatchers.slice());
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRadioValue((event.target as HTMLInputElement).value);
  };

  const margeConditions = (eventMatcher: EventMatcher) => {
    const conditions = eventMatcher.conditions
      // @ts-ignore
      .map((condition) => {
        if (condition) {
          return {
            data: {
              args: condition.data ? [condition.data.args] : undefined,
              method: condition.data ? condition.data.method : undefined,
              type: condition.data ? condition.data.type : undefined,
            },
            type: getComparatorType(condition.data.comparator),
          };
        }
      })
      .filter((v) => v);
    conditions.push({
      data: {
        // @ts-ignore
        conditions: eventMatcher.etSelector.data.conditions
          // @ts-ignore
          .map((condition) => {
            if (condition) {
              return {
                data: {
                  args: condition.data ? [condition.data.args] : undefined,
                  method: condition.data ? condition.data.method : undefined,
                  type: condition.data ? condition.data.type : undefined,
                },
                type: getComparatorType(condition.type),
              };
            }
          })
          .filter((v) => v),
      },
      type: 'EtSelector',
    });
    return conditions;
  };

  /* eslint no-empty-pattern: 0 */
  const [runUpdateGoal, { loading }] = useMutation(PutConversionGoalDocument, {
    context: { clientName: 'planbcd' },
    variables: {
      id: goal.id,
      input: {
        id: goal.id,
        goal: {
          created_at: goal.created_at,
          cv_type: goal.cv_type,
          cv_value: goal.cv_value,
          cv_value_currency: goal.cv_value_currency,
          description: description,
          // @ts-ignore
          goal_tags: selectedTags.map((tag) => {
            if (tag.selected) {
              return {
                href: tag.tag.href,
                id: tag.tag.id,
                name: tag.tag.name,
                sort_order: tag.tag.sort_order,
                type: tag.tag.type,
                selected: tag.selected,
              };
            }
          }),
          href: goal.href,
          lift_report_goal_config_summary: goal.lift_report_goal_config_summary,
          projects_count: goal.projects_count,
          title: title,
          transaction_type: goal.transaction_type,
          trigger: {
            type: goal.trigger.type,
            device_type: goal.trigger.type === 'event' ? radioValue : undefined,
            // @ts-ignore
            url_matchers: goal.trigger.url_matchers?.map((urlMatcher) => {
              if (urlMatcher && urlMatcher.expression.length > 0) {
                return {
                  created_at: urlMatcher.created_at,
                  updated_at: urlMatcher.updated_at,
                  href: urlMatcher.href,
                  triggerable: urlMatcher.triggerable,
                  expression: urlMatcher.expression,
                  is_inclusion: urlMatcher.is_inclusion,
                  match_type: urlMatcher.match_type,
                  id: urlMatcher.id,
                };
              }
            }),
            // @ts-ignore
            url_matchers_attributes: urlMatchers.map((urlMatcher) => {
              if (urlMatcher.expression.length > 0) {
                return {
                  id: urlMatcher.id && urlMatcher.id.length > 0 ? urlMatcher.id : undefined,
                  expression: urlMatcher.expression,
                  is_inclusion: urlMatcher.isInclusion,
                  match_type: urlMatcher.matchType,
                  _destroy: urlMatcher.destroy,
                };
              }
            }),
            event_matchers: goal.trigger.event_matchers
              ? goal.trigger.event_matchers.map((eventMatcher) => {
                  return {
                    comparator: eventMatcher?.data.comparator,
                    event: eventMatcher?.data.event,
                    id: eventMatcher?.id,
                    name: eventMatcher?.data.name,
                    type: 'string',
                    conditions: eventMatcher?.data.conditions,
                  };
                })
              : undefined,
            event_matchers_attributes: eventMatchers.map((eventMatcher) => {
              return {
                type: 'EtEvent',
                id: eventMatcher.id,
                _destroy: eventMatcher.destroy,
                data: {
                  event: eventMatcher.event,
                  name: eventMatcher.name,
                  conditions: margeConditions(eventMatcher),
                },
              };
            }),
          },
          updated_at: goal.updated_at,
          isNew: false,
        },
      },
    },
    onCompleted: () => {
      closePostDrawer();
      refetch();
    },
    onError: (e) => {
      console.log('onError', e);
      if (e.message.search(/^(?=.*422).*$/) > -1) {
        if (drawerType === 'NAME') {
          globalState.setErrors({
            message: 'エラーが発生しました。 名前は既に存在します',
            name: 'NonFatalError',
          });
        } else if (drawerType === 'EVENT') {
          globalState.setErrors({
            message: '正しく設定されていない項目があるためゴールを保存できません。',
            name: 'NonFatalError',
          });
        }
      }
      if (e.message.search(/^(?=.*403).*$/) > -1) {
        globalState.setErrors({
          message: 'エラーが発生しました。 権限がありません',
          name: 'NonFatalError',
        });
      }
      if (e.message.search(/^(?=.*null).*$/) > -1) {
        globalState.setErrors({
          message: 'エラーが発生しました。 RECORD NOT FOUND',
          name: 'NonFatalError',
        });
      }
    },
  });

  return (
    <div className={className}>
      <StyledBackdrop open={loading}>
        <CircularProgress color="inherit" />
      </StyledBackdrop>
      <StyledDrawer
        anchor="right"
        open={open}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            togglePostDrawer(false);
          }
        }}
      >
        <DrawerContainer>
          <StyledDrawerContent>
            <DrawerHeader>
              <DrawerTitle>{header}</DrawerTitle>
            </DrawerHeader>
            <Grid container>
              {drawerType === 'TAG' ? (
                <TagContent selectedTags={selectedTags} setSelectedTags={setSelectedTags} />
              ) : null}
              {drawerType === 'NAME' ? (
                <NameContent
                  title={title}
                  setTitle={setTitle}
                  description={description}
                  setDescription={setDescription}
                />
              ) : null}
              {drawerType === 'URL' ? (
                <UrlContent
                  goal={goal}
                  radioValue={radioValue}
                  handleRadioChange={handleRadioChange}
                  urlMatchers={urlMatchers}
                  setUrlMatchers={setUrlMatchers}
                  removeUrl={removeUrl}
                  addExclusionUrl={addExclusionUrl}
                  addInclusionUrl={addInclusionUrl}
                />
              ) : null}
              {drawerType === 'EVENT' ? (
                <EventContent
                  goal={goal}
                  siteViewerUrl={siteViewerUrl}
                  setSiteViewerUrl={setSiteViewerUrl}
                  eventMatchers={eventMatchers}
                  setEventMatchers={setEventMatchers}
                  removeEventMatcher={removeEventMatcher}
                  removeEventFilter={removeEventFilter}
                  addEventFilter={addEventFilter}
                  removeEtSelector={removeEtSelector}
                  addEtSelector={addEtSelector}
                  addEventMatcher={addEventMatcher}
                  addEventMatcherBySiteViewer={addEventMatcherBySiteViewer}
                  addInclusionUrlBySiteViewer={addInclusionUrlBySiteViewer}
                />
              ) : null}
            </Grid>
          </StyledDrawerContent>
          <DrawerFooter>
            <DrawerCancelButton onClick={closePostDrawer}>キャンセル</DrawerCancelButton>
            <DrawerButton
              disabled={(drawerType === 'NAME' && title.length === 0) || (drawerType === 'NAME' && title.length > 50)}
              onClick={() => {
                runUpdateGoal();
              }}
            >
              保存する
            </DrawerButton>
          </DrawerFooter>
        </DrawerContainer>
      </StyledDrawer>
    </div>
  );
};

const StyledDrawerContent = styled(DrawerContent)`
  width: 50vw;
  min-width: 540px;
`;

export default GoalSettingEditDrawer;
